/**
 * creature/templates/wolf.ts — The Untamed
 *
 * Wolves are not dogs. They move differently — lower to the ground,
 * more deliberate, always watching. The dire wolf is larger still,
 * fierce enough to ride into battle. Both are pack creatures to their core.
 */

import type { SkeletonDef } from "../skeleton.js";
import type { SpeciesTemplate, GaitPattern } from "../species.js";
import { registerSpecies, createBreed } from "../species.js";

// ─── Wolf Skeleton ──────────────────────────────────────────────────────────

const WOLF_SKELETON: SkeletonDef = {
  id: "skeleton_wolf",
  rootJointId: "hip",
  joints: [
    {
      id: "hip", parentId: null,
      restOffset: { x: 0, y: 0 }, restAngle: -0.35,
      minAngle: -1.3, maxAngle: 0.2, length: 14,
      tags: ["root", "hip"],
    },
    {
      id: "shoulder", parentId: "hip",
      restOffset: { x: 0, y: 0 }, restAngle: 0,
      minAngle: -0.3, maxAngle: 0.3, length: 7,
      tags: ["shoulder", "spine"],
    },
    {
      id: "neck", parentId: "shoulder",
      restOffset: { x: 0, y: 0 }, restAngle: -0.4,
      minAngle: -1.2, maxAngle: 0.3, length: 4.5,
      tags: ["neck"],
    },
    {
      id: "head", parentId: "neck",
      restOffset: { x: 0, y: 0 }, restAngle: 0,
      minAngle: -0.5, maxAngle: 0.5, length: 0,
      tags: ["head"],
    },
    // Tail — thick and bushy
    {
      id: "tail_0", parentId: "hip",
      restOffset: { x: 5, y: 0 }, restAngle: -0.2,
      minAngle: -1.5, maxAngle: 1.0, length: 7,
      tags: ["tail"],
    },
    {
      id: "tail_1", parentId: "tail_0",
      restOffset: { x: 0, y: 0 }, restAngle: -0.3,
      minAngle: -1.5, maxAngle: 1.5, length: 6,
      tags: ["tail"],
    },
    {
      id: "tail_2", parentId: "tail_1",
      restOffset: { x: 0, y: 0 }, restAngle: -0.2,
      minAngle: -1.5, maxAngle: 1.5, length: 4,
      tags: ["tail", "tail_tip"],
    },
    // Ears — pointed, alert
    {
      id: "ear_l", parentId: "head",
      restOffset: { x: -2.2, y: -1.5 }, restAngle: -1.4,
      minAngle: -2.0, maxAngle: -0.5, length: 4,
      tags: ["ear", "ear_l"],
    },
    {
      id: "ear_r", parentId: "head",
      restOffset: { x: 1.8, y: -1.8 }, restAngle: -1.4,
      minAngle: -2.0, maxAngle: -0.5, length: 4,
      tags: ["ear", "ear_r"],
    },
  ],
  drawOrder: [
    { name: "back_legs",   joints: ["hip"] },
    { name: "tail",        joints: ["hip", "tail_0", "tail_1", "tail_2"] },
    { name: "body",        joints: ["hip", "shoulder"] },
    { name: "front_legs",  joints: ["shoulder"] },
    { name: "head",        joints: ["head", "neck", "ear_l", "ear_r"] },
    { name: "fire_glow",   joints: ["hip", "shoulder"] },
  ],
  ikChains: [
    {
      id: "look_at",
      jointIds: ["shoulder", "neck", "head"],
      targetMode: "point",
      iterations: 3,
      tolerance: 0.5,
    },
  ],
};

// ─── Wolf Gaits ─────────────────────────────────────────────────────────────

const WOLF_WALK: GaitPattern = {
  name: "walk",
  speedRange: [0, 3],
  legPhaseOffsets: {
    leg_front_l: 0, leg_front_r: Math.PI,
    leg_back_l: Math.PI * 0.5, leg_back_r: Math.PI * 1.5,
  },
  strideLength: 0.55,
  bodyBob: 0.3,
  bodyRoll: 0.1,
  headBob: 0.15,
};

const WOLF_TROT: GaitPattern = {
  name: "trot",
  speedRange: [3, 6],
  legPhaseOffsets: {
    leg_front_l: 0, leg_front_r: Math.PI,
    leg_back_l: Math.PI, leg_back_r: 0,
  },
  strideLength: 0.85,
  bodyBob: 0.45,
  bodyRoll: 0.15,
  headBob: 0.2,
};

const WOLF_GALLOP: GaitPattern = {
  name: "gallop",
  speedRange: [6, 12],
  legPhaseOffsets: {
    leg_front_l: 0, leg_front_r: 0.4,
    leg_back_l: Math.PI, leg_back_r: Math.PI + 0.4,
  },
  strideLength: 1.5,
  bodyBob: 0.8,
  bodyRoll: 0.08,
  headBob: 0.1,
};

// ─── Wolf Template ──────────────────────────────────────────────────────────

export const SPECIES_WOLF: SpeciesTemplate = {
  id: "wolf",
  displayName: "Wolf",
  bodyPlan: "quadruped",
  skeleton: WOLF_SKELETON,
  size: "large",
  baseSpeed: 3.0,
  heightAtShoulder: 0.85,
  gaits: [WOLF_WALK, WOLF_TROT, WOLF_GALLOP],
  turnRate: Math.PI * 1.2,
  palette: {
    body: "#707068",
    markings: "#505048",
    eyes: "#c0a020",
    nose: "#2a2a28",
    accent: "#404038",
    belly: "#909088",
  },
  bodyShape: {
    bodyLength: 14,
    bodyWidth: 4.5,
    headRadius: 4.0,
    tailSegments: 3,
    tailLength: 17,
    earStyle: "pointed",
    earSize: 1.1,
    legThickness: 2.0,
    hasMane: false,
    hasWings: false,
    neckLength: 1.2,
    snoutLength: 0.7,
    legLength: 1.3,
  },
  behaviorDefaults: {
    energy: 0.5,
    sociability: 0.3,
    curiosity: 0.5,
    obedience: 0.1,
    playfulness: 0.3,
    courage: 0.8,
    agility: 0.8,
    stamina: 0.9,
    nocturnality: 0.5,
    territoriality: 0.9,
    packInstinct: 0.95,
  },
  rideable: false,
  riderAttachJoint: "",
  riderOffsetZ: 0,
  saddleable: false,
  sounds: {
    idle: ["breathe_deep"],
    happy: ["yip"],
    hungry: ["whine_low"],
    walk: ["paw_forest"],
    run: ["paw_rapid_heavy"],
    special: ["howl", "howl_long", "growl_deep", "snarl", "bark_sharp"],
  },
  moodContribution: {
    warmth: 0.02,
    coziness: 0.03,
    energy: 0.08,
    nature: 0.1,
  },
};

// ─── Dire Wolf ──────────────────────────────────────────────────────────────

export const SPECIES_DIRE_WOLF = createBreed(SPECIES_WOLF, {
  id: "dire_wolf",
  displayName: "Dire Wolf",
  size: "huge",
  baseSpeed: 3.5,
  heightAtShoulder: 1.3,
  rideable: true,
  riderAttachJoint: "shoulder",
  riderOffsetZ: 0.8,
  saddleable: true,
  palette: {
    ...SPECIES_WOLF.palette,
    body: "#3a3a38",
    markings: "#282828",
    eyes: "#ff6020",
    belly: "#505050",
  },
  bodyShape: {
    ...SPECIES_WOLF.bodyShape,
    bodyLength: 18,
    bodyWidth: 5.5,
    headRadius: 5.0,
    tailLength: 20,
    legThickness: 2.8,
    legLength: 1.5,
    earSize: 1.3,
  },
  behaviorDefaults: {
    ...SPECIES_WOLF.behaviorDefaults,
    courage: 0.95,
    energy: 0.6,
    territoriality: 0.95,
  },
});

// ─── Arctic Wolf ────────────────────────────────────────────────────────────

export const SPECIES_ARCTIC_WOLF = createBreed(SPECIES_WOLF, {
  id: "arctic_wolf",
  displayName: "Arctic Wolf",
  palette: {
    ...SPECIES_WOLF.palette,
    body: "#e0e0d8",
    markings: "#c8c8c0",
    eyes: "#70a8d0",
    belly: "#f0f0e8",
    accent: "#d0d0c8",
  },
  behaviorDefaults: {
    ...SPECIES_WOLF.behaviorDefaults,
    stamina: 0.95,
  },
});

// ─── Register ───────────────────────────────────────────────────────────────

export function registerWolves(): void {
  registerSpecies(SPECIES_WOLF);
  registerSpecies(SPECIES_DIRE_WOLF);
  registerSpecies(SPECIES_ARCTIC_WOLF);
}
